import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import memoryAdapter from 'pouchdb-adapter-memory';
import { addRxPlugin, createRxDatabase } from 'rxdb';
import { RxDBCleanupPlugin } from 'rxdb/plugins/cleanup';
import { RxDBDevModePlugin } from 'rxdb/plugins/dev-mode';
import { RxStorageLokiStatics } from 'rxdb/plugins/lokijs';
import { RxDBMigrationPlugin } from 'rxdb/plugins/migration';
import { addPouchPlugin, getRxStoragePouch } from 'rxdb/plugins/pouchdb';
import { RxDBQueryBuilderPlugin } from 'rxdb/plugins/query-builder';
import { RxDBReplicationGraphQLPlugin } from 'rxdb/plugins/replication-graphql';
import { RxDBUpdatePlugin } from 'rxdb/plugins/update';
import { RxDBValidatePlugin } from 'rxdb/plugins/validate';
import { getRxStorageWorker } from 'rxdb/plugins/worker';

import { AimsDatabaseCollections } from '../models/models';
import { isJestTest } from '../test-helpers/isJestTest';
import { assetDetailsSchema } from './assetDetails/assetDetails.schema';
import { assetDetailsMigrationStrategies } from './assetDetails/assetDetails.migration';
import { assetsByAreaHeirachySchema } from './assetsByAreaHeirachy/assetsByAreaHierarchy.schema';
import { assetSectionDefinationsListMigrationStrategies } from './assetSectionDefination/assetSectionDefinition.migration';
import { assetSectionDefinationsListSchema } from './assetSectionDefination/assetSectionDefinition.schema';
import { componentChecklistSchema } from './componentChecklist/queryBuilder';
import { componentRemovalCategoriesSchema } from './componentRemovalCategories/queryBuilder';
import { componentTypeSchema } from './componentType/queryBuilder';
import { conditionRatingSchema } from './conditionRating/queryBuilder';
import { deficiencyActionedListSchema } from './deficiencyActionedList/queryBuilder';
import {
  deficiencyCommentsMigrationStrategies,
  deficiencyCommentsSchema,
} from './deficiencyComments/queryBuilder';
import { deficiencyComponentRemovalSchema } from './deficiencyComponentRemoval/queryBuilder';
import {
  deficiencyFilesMigrationStrategies,
  deficiencyFilesSchema,
} from './deficiencyFiles/queryBuilder';
import {
  deficiencyListMigrationStrategies,
  deficiencyListSchema,
} from './deficiencyList/queryBuilder';
import { deficiencyVersionFilesSchema } from './deficiencyVersionFiles/queryBuilder';
import {
  elevationRangeMigrationStrategies,
  elevationRangeSchema,
} from './elevationRange/queryBuilder';
import { environmentalConditionSchema } from './environmentalCondition/queryBuilder';
import { plannedDeficiencyListSchema } from './existingDeficiencyList/queryBuilder';
import { inspectionFilesSchema } from './inspectionFiles/queryBuilder';
import {
  inspectionListSchema,
  inspectionsListMigrationStrategies,
} from './inspectionList/queryBuilder';
import {
  lineandSectionPropertiesListMigrationStrategies,
  lineandSectionPropertiesListSchema,
} from './lineandSectionPropertiesDetails/queryBuilder';
import { notificationMigrationStrategies, notificationSchema } from './notification/queryBuilder';
import { operationalDowntimeSchema } from './operationalDowntime/queryBuilder';
import { plannedDeficiencyFilesSchema } from './plannedDeficiencyFiles/queryBuilder';
import { pmCheckListMigrationStrategies, pmCheckListSchema } from './pmCheckList/queryBuilder';
import {
  previousReportFilesMigrationStrategies,
  previousReportFilesSchema,
} from './previousReports/queryBuilder';
import {
  previousTaskCommentsMigrationStrategies,
  previousTaskCommentsSchema,
} from './previousTaskComments/queryBuilder';
import { primaryCauseOfFailureSchema } from './primaryCauseOfFailure/queryBuilder';
import { remedialActionSchema } from './remedialAction/queryBuilder';
import { repairFactorSchema } from './repairFactor/queryBuilder';
import {
  riskAssessmentMigrationStrategies,
  riskAssessmentSchema,
} from './riskAssessment/queryBuilder';
import { savePmChecklistSchema } from './savepmChecklist/queryBuilder';
import {
  signAndStampFileMigrationStrategies,
  signAndStampFileSchema,
} from './signAndStamp/queryBuilder';
import { signAndStampUserConsentSchema } from './signAndStampUserConsent/queryBuilder';
import {
  signAndStampUserLevelMigrationStrategies,
  signAndStampUserLevelSchema,
} from './signAndStampUserLevel/queryBuilder';
import { taskListSchema, taskListSchemaMigrationStrategies } from './taskList/queryBuilder';
import { trafficSafetyScoreSchema } from './trafficSafetyScore/queryBuilder';
import { urgencySchema } from './urgency/queryBuilder';
import {
  viewInspectorMigrationStrategies,
  viewInspectorSchema,
} from './viewInspector/queryBuilder';
import { workPackageFilesSchema } from './workPackageFiles/queryBuilder';
import { workPackagesMigrationStrategies, workPackagesSchema } from './workPackages/queryBuilder';

addRxPlugin(RxDBValidatePlugin);
addRxPlugin(RxDBDevModePlugin);
addRxPlugin(RxDBQueryBuilderPlugin);
addRxPlugin(RxDBMigrationPlugin);
addRxPlugin(RxDBUpdatePlugin);
addRxPlugin(RxDBReplicationGraphQLPlugin);
addRxPlugin(RxDBCleanupPlugin);
addPouchPlugin(memoryAdapter);
const currentTimestamp = dayjs().unix();
const DEBUG_RXDB = localStorage.getItem('DEBUG_RXDB') === 'true';

const initializeDB = async () => {
  // create RxDB instance

  let db;
  try {
    // Create RxDB Instance
    if (DEBUG_RXDB) console.log('Creating Rxdb 12 db...');
    db = await createRxDatabase<AimsDatabaseCollections>({
      name: 'aims_inspection_rxdb',
      storage: isJestTest()
        ? getRxStoragePouch('memory')
        : getRxStorageWorker({
            /**
             * The static methods of the RxStorage that is also
             * used inside of the worker process.
             */
            statics: RxStorageLokiStatics,
            /**
             * Contains any value that can be used as parameter
             * to the Worker constructor of thread.js
             * Most likely you want to put the path to the worker.js file in here.
             *
             * @link https://github.com/andywer/threads.js/
             */
            workerInput: '/lokijs-incremental-indexeddb.worker.js',
          }),

      ignoreDuplicate: true,
      cleanupPolicy: {
        minimumDeletedTime: 1000 * 60 * 60 * 24 * 14, // 14 Days,
      },
    });
  } catch (error) {
    console.log('🚀 ~ file: initializeDB.ts ~ line 138 ~ initializeDB ~ error', error); // eslint-disable-line no-console
    // console logs will be removed once sentry implementation is done
    Sentry.captureException(error, {
      tags: {
        initializationError: true,
        rxDBError: true,
        currentTimestamp,
      },
    });

    // Always refresh in case of database intialization error
    if (!DEBUG_RXDB) window.location.reload();
  }

  // setup rxdb collections
  try {
    await db.addCollections({
      work_packages: {
        schema: workPackagesSchema,
        migrationStrategies: workPackagesMigrationStrategies,
      },
      view_inspector: {
        schema: viewInspectorSchema,
        migrationStrategies: viewInspectorMigrationStrategies,
      },
      pm_checklist: {
        schema: pmCheckListSchema,
        migrationStrategies: pmCheckListMigrationStrategies,
      },
      save_pmchecklist: {
        schema: savePmChecklistSchema,
      },
      inspection_list: {
        schema: inspectionListSchema,
        migrationStrategies: inspectionsListMigrationStrategies,
      },
      task_list: {
        schema: taskListSchema,
        migrationStrategies: taskListSchemaMigrationStrategies,
      },
      work_package_files: {
        schema: workPackageFilesSchema,
      },
      asset_details: {
        schema: assetDetailsSchema,
        migrationStrategies: assetDetailsMigrationStrategies,
      },
      inspection_files: {
        schema: inspectionFilesSchema,
      },
      sign_and_stamp_file: {
        schema: signAndStampFileSchema,
        migrationStrategies: signAndStampFileMigrationStrategies,
      },
      previous_reports_files: {
        schema: previousReportFilesSchema,
        migrationStrategies: previousReportFilesMigrationStrategies,
      },
      deficiency_list: {
        schema: deficiencyListSchema,
        migrationStrategies: deficiencyListMigrationStrategies,
      },
      deficiency_actioned_list: {
        schema: deficiencyActionedListSchema,
      },
      component_removal_categories: {
        schema: componentRemovalCategoriesSchema,
      },
      deficiency_component_removal: {
        schema: deficiencyComponentRemovalSchema,
      },
      risk_assessment: {
        schema: riskAssessmentSchema,
        migrationStrategies: riskAssessmentMigrationStrategies,
      },
      deficiency_files: {
        schema: deficiencyFilesSchema,
        migrationStrategies: deficiencyFilesMigrationStrategies,
      },
      primary_cause_of_failure: {
        schema: primaryCauseOfFailureSchema,
      },
      remedial_action: {
        schema: remedialActionSchema,
      },
      condition_rating: {
        schema: conditionRatingSchema,
      },
      operational_downtime: {
        schema: operationalDowntimeSchema,
      },
      traffic_safety_score: {
        schema: trafficSafetyScoreSchema,
      },
      environmental_condition: {
        schema: environmentalConditionSchema,
      },
      repair_factor: {
        schema: repairFactorSchema,
      },
      urgency: {
        schema: urgencySchema,
      },
      component_type: {
        schema: componentTypeSchema,
      },
      component_checklist: {
        schema: componentChecklistSchema,
      },
      assets_by_area_heirachy: {
        schema: assetsByAreaHeirachySchema,
      },
      notification: {
        schema: notificationSchema,
        migrationStrategies: notificationMigrationStrategies,
      },
      previous_task_comments: {
        schema: previousTaskCommentsSchema,
        migrationStrategies: previousTaskCommentsMigrationStrategies,
      },
      deficiency_comments: {
        schema: deficiencyCommentsSchema,
        migrationStrategies: deficiencyCommentsMigrationStrategies,
      },
      sign_and_stamp_user_consent: {
        schema: signAndStampUserConsentSchema,
      },
      sign_and_stamp_user_level: {
        schema: signAndStampUserLevelSchema,
        migrationStrategies: signAndStampUserLevelMigrationStrategies,
      },
      elevation_range: {
        schema: elevationRangeSchema,
        migrationStrategies: elevationRangeMigrationStrategies,
      },
      planned_deficiency_list: {
        schema: plannedDeficiencyListSchema,
      },
      planned_deficiency_files: {
        schema: plannedDeficiencyFilesSchema,
      },
      line_and_section_properties_list: {
        schema: lineandSectionPropertiesListSchema,
        migrationStrategies: lineandSectionPropertiesListMigrationStrategies,
      },
      asset_section_definations_list: {
        schema: assetSectionDefinationsListSchema,
        migrationStrategies: assetSectionDefinationsListMigrationStrategies,
      },
      deficiency_component_removal_version: {
        schema: deficiencyComponentRemovalSchema,
      },
      deficiency_version_comments: {
        schema: deficiencyCommentsSchema,
        migrationStrategies: deficiencyCommentsMigrationStrategies,
      },
      deficiency_version_files: {
        schema: deficiencyVersionFilesSchema,
      },
    });
  } catch (error) {
    console.log('🚀 ~ file: initializeDB.ts ~ line 298 ~ initializeDB ~ error', error); // eslint-disable-line no-console
    // console logs will be removed once sentry implementation is done
    Sentry.captureException(error, {
      tags: {
        initializationError: true,
        rxDBError: true,
        currentTimestamp,
      },
    });
    // Always refresh in case of database intialization error
    if (!DEBUG_RXDB) window.location.reload();
  }
  return { db };
};

export default initializeDB;
